import type { LocaleContent } from "@/types/site";

export const enContent: LocaleContent = {
  site: {
    description:
      "Event design, decoration, planning, vendor coordination, and day-of management for refined celebrations in Puerto Rico.",
    name: "JPL Atelier",
    tagline:
      "Quiet-luxury planning for weddings, corporate events, and select celebrations.",
  },
  navigation: [
    { href: "/about", label: "About" },
    { href: "/services", label: "Services" },
    { href: "/process", label: "Process" },
  ],
  footerGroups: [
    {
      title: "Explore",
      links: [
        { href: "/about", label: "About" },
        { href: "/services", label: "Services" },
        { href: "/process", label: "Process" },
      ],
    },
    {
      title: "Plan",
      links: [
        { href: "/inquiry", label: "Start an Inquiry" },
        { href: "/legal", label: "Privacy & Terms" },
      ],
    },
  ],
  pages: {
    home: {
      eyebrow: "Luxury event atelier",
      title:
        "Event planning and design for celebrations with a clear point of view.",
      description:
        "JPL Atelier is the digital home of Joleany Event Planner: design, decoration, planning, vendor coordination, and day-of management for weddings, corporate events, luxury events, and senior celebrations 18+.",
    },
    about: {
      eyebrow: "About the atelier",
      title: "A planning studio led with taste, order, and close attention.",
      description:
        "Meet Joleany’s approach to design, planning, and on-site execution.",
    },
    services: {
      eyebrow: "Services",
      title: "Design, decor, planning, and coordination for polished events.",
      description:
        "Find the service that fits where you are in the planning process—from first ideas to day-of management.",
    },
    process: {
      eyebrow: "Process",
      title: "A planning flow that keeps decisions clear.",
      description:
        "The process moves through intake, moodboard, space planning, digital decor, vendors, and day-of oversight.",
    },
    lookbook: {
      eyebrow: "Lookbook",
      title: "The visual direction behind the atelier.",
      description:
        "Browse mood, styling references, and detail studies that show the brand’s quiet-luxury taste.",
    },
    inquiry: {
      eyebrow: "Inquiry",
      title: "Tell us what you’re planning.",
      description:
        "Share the event type, location, priorities, and where you need help. The studio will recommend the right service.",
    },
    legal: {
      eyebrow: "Privacy and Terms",
      title: "Privacy and terms",
      description:
        "A plain overview of inquiry information, website content, and next-step confirmations.",
    },
  },
  home: {
    hero: {
      eyebrow: "JPL Atelier by Joleany",
      title: "Curated Events\nDesigned With Intention",
      description:
        "Design, decor, and planning for weddings, corporate gatherings, senior celebrations, and select private events.",
      ctaHref: "/inquiry",
      ctaLabel: "Start an Inquiry",
      secondaryHref: "/services",
      secondaryLabel: "Explore Services",
    },
    brandIntro: {
      eyebrow: "The atelier approach",
      title: "Clear design. Careful planning. A calmer event day.",
      body: [
        "JPL Atelier is the online home of Joleany Event Planner, a Puerto Rico event studio focused on design, physical and digital decor, full planning, vendor coordination, and day-of management.",
        "The work is clean, warm, and restrained. Each project starts with the look and the space, then moves into vendors, timing, setup, and the event day itself.",
      ],
      highlights: [
        "Founder-led direction from first ideas to final setup.",
        "Moodboard, space, decor, and vendors planned as one system.",
        "Calm coordination for weddings, corporate events, luxury gatherings, and senior celebrations 18+.",
      ],
    },
    servicesPreview: {
      eyebrow: "Services",
      title: "Choose the help your event actually needs.",
      description:
        "Services are organized around real planning needs: defining the look, decorating the space, managing vendors, or overseeing the day of the event.",
      items: [
        {
          title: "Event Design",
          description:
            "Moodboards, palettes, layouts, and styling notes that define the look before production starts.",
          image: "/Forest-Photos/Forest-1.png",
        },
        {
          title: "Decoration",
          description:
            "Physical and digital decor planning for the ceremony, reception, focal areas, and guest-facing details.",
          image: "/Forest-Photos/Forest-2.png",
        },
        {
          title: "Full Event Planning",
          description:
            "Planning across logistics, vendors, design, timelines, and day-of details.",
          image: "/Forest-Photos/Forest-3.png",
        },
      ],
    },
    processPreview: {
      eyebrow: "Process",
      title: "A steady path to event day.",
      description:
        "The process gives each decision a place: intake, look, space, vendors, setup, and final oversight.",
      image: "/Forest-Photos/Forest-4.png",
      steps: [
        {
          title: "Define the Vision",
          description:
            "The inquiry captures the event type, priorities, guest experience, and level of help needed.",
        },
        {
          title: "Design the Experience",
          description:
            "Moodboards, space planning, and digital decor previews settle the look before production begins.",
        },
        {
          title: "Coordinate the Execution",
          description:
            "Vendor communication, final confirmations, setup supervision, and day-of management keep the day organized.",
        },
      ],
    },
    eventTypes: {
      eyebrow: "Event types",
      title:
        "For weddings, corporate events, luxury gatherings, and senior celebrations.",
      description:
        "The studio focuses on events where design, guest flow, and professional coordination need to be handled carefully.",
      items: [
        {
          title: "Weddings",
          description:
            "Wedding celebrations with a clear design direction, careful planning, and steady day-of management.",
        },
        {
          title: "Corporate Parties",
          description:
            "Company gatherings and brand moments with a polished atmosphere and a professional flow.",
        },
        {
          title: "Luxury Events",
          description:
            "Private celebrations for clients who want a restrained look, strong details, and a point of view that feels their own.",
        },
        {
          title: "Senior Celebrations 18+",
          description:
            "Milestone celebrations with a mature look, strong styling, and a more editorial feel.",
        },
      ],
    },
    cta: {
      eyebrow: "Start with the details",
      title: "Planning a celebration that needs design direction?",
      description:
        "Send the event type, location, priorities, and planning stage. The studio will help you choose the right next step.",
      href: "/inquiry",
      label: "START AN INQUIRY",
    },
  },
  sections: {
    aboutFounder: {
      eyebrow: "Founder-led planning",
      title: "A calm planning eye behind the work.",
      body: [
        "Joleany leads each event with a clear design point of view, an organized planning rhythm, and close attention to the details guests actually notice.",
      ],
      highlights: [
        "Creative direction shaped by luxury, restraint, and warm detail.",
        "Planning that keeps the look, vendors, timing, and setup connected.",
      ],
    },
    aboutPhilosophy: {
      eyebrow: "Design philosophy",
      title: "Quiet luxury means knowing what to leave out.",
      description:
        "A strong event does not need excess. Joleany builds the moodboard, layout, decor, and logistics around the feeling the celebration should carry, then edits until the result feels clean and complete.",
    },
    aboutDifferentiators: {
      eyebrow: "What makes us different",
      title: "A process that makes the event easier to see before it is built.",
      description:
        "This is not casual party styling. The work starts by defining the look, reading the space, mapping vendor needs, and giving the client a clearer picture before setup begins.",
      items: [
        {
          title: "Clear planning sequence",
          description:
            "The work moves through intake, moodboard, space planning, digital decor, vendors, and day-of management.",
        },
        {
          title: "A defined visual direction",
          description:
            "Each concept is built around proportion, texture, palette, and the tone of the occasion.",
        },
        {
          title: "Physical and digital decoration",
          description:
            "Decor is planned on screen and in the space, so clients can understand the setup before production starts.",
        },
        {
          title: "Vendor coordination",
          description:
            "Vendors receive the concept, timing, production notes, and final confirmations they need to work from the same plan.",
        },
        {
          title: "Day-of management",
          description:
            "On event day, the team watches the setup, timeline, vendor flow, and guest-facing details.",
        },
      ],
    },
    servicesList: {
      eyebrow: "Service overview",
      title: "Choose the service that fits where your event stands.",
      description:
        "Each service answers a different need: the look, the decor, the full plan, the vendors, or the day-of management.",
      navigatorTitle:
        "Use the details below to see what each service covers and when it makes sense.",
      serviceCountLabel: "services",
      decisionTitle: "Still deciding where to begin?",
      decisionDescription:
        "Use your current planning stage to choose the best starting point.",
      items: [
        {
          title: "Event Design",
          summary:
            "Creative direction for events that need the look defined before bigger planning decisions are made.",
          description:
            "Event Design defines the mood, palette, layout references, and styling direction before production decisions begin.",
          audience:
            "Best for clients who know the event should feel polished but need help choosing the look, palette, and atmosphere.",
          highlights: [
            "Moodboard development",
            "Color palette direction",
            "Concept direction",
            "Tablescape inspiration",
            "Layout and styling guidance",
            "Design notes across key details",
          ],
          ctaHref: "/inquiry",
          ctaLabel: "Inquire about event design",
        },
        {
          title: "Decoration",
          summary:
            "Physical and digital decor planning for clients who need the space to feel finished and guest-ready.",
          description:
            "Decoration turns the design direction into a real setup through styling, placement, digital previews, and setup notes.",
          audience:
            "Best for clients who have a mood or theme but need help making it work in the actual space.",
          highlights: [
            "Physical decoration planning",
            "Digital decoration concepts",
            "Setup direction",
            "Decor styling guidance",
            "Coordination across key areas",
          ],
          ctaHref: "/inquiry",
          ctaLabel: "Inquire about decoration",
        },
        {
          title: "Full Event Planning",
          summary:
            "Full planning for clients who want design, logistics, vendors, and day-of details managed together.",
          description:
            "Full Event Planning gives the event one planning track, from early structure through the final day-of details.",
          audience:
            "Best for clients who do not want to manage design, vendors, timelines, and logistics in separate pieces.",
          highlights: [
            "Planning structure",
            "Timeline guidance",
            "Vendor coordination",
            "Design direction",
            "Event logistics",
            "Day-of oversight",
          ],
          ctaHref: "/inquiry",
          ctaLabel: "Inquire about full planning",
        },
        {
          title: "Vendor Coordination",
          summary:
            "Communication support so selected vendors understand the look, timing, responsibilities, and expectations.",
          description:
            "Vendor Coordination keeps quotes, responsibilities, timing, and final details organized before production and setup.",
          audience:
            "Best for clients who have started choosing vendors but need clearer follow-up and stronger organization.",
          highlights: [
            "Vendor search support",
            "Quote coordination",
            "Communication guidance",
            "Vendor notes tied to the event concept",
            "Final vendor confirmations",
          ],
          ctaHref: "/inquiry",
          ctaLabel: "Inquire about vendor coordination",
        },
        {
          title: "Day-of Event Management",
          summary:
            "On-site oversight for events that are already planned but need steady management on the day itself.",
          description:
            "Day-of Event Management covers setup, timing, vendor communication, and key logistics while the event is happening.",
          audience:
            "Best for clients who have the plan in place and want someone experienced watching the moving pieces.",
          highlights: [
            "Day-of timeline",
            "Vendor check-ins",
            "Setup supervision",
            "Guest flow support",
            "Logistics management",
            "Final day-of oversight",
          ],
          ctaHref: "/inquiry",
          ctaLabel: "Inquire about day-of management",
        },
      ],
      decisionItems: [
        {
          title: "The look is still undefined",
          description:
            "Start with Event Design when the main question is how the celebration should look and feel.",
          service: "Event Design",
        },
        {
          title: "The mood is clear, but the space is not",
          description:
            "Start with Decoration when the idea exists but the setup still needs placement, styling, and practical direction.",
          service: "Decoration",
        },
        {
          title: "You want one planning partner",
          description:
            "Start with Full Event Planning when design, logistics, vendors, and day-of details should be managed together.",
          service: "Full Event Planning",
        },
        {
          title: "Vendors are involved, but the plan feels loose",
          description:
            "Start with Vendor Coordination when communication, quotes, responsibilities, and confirmations need a clearer system.",
          service: "Vendor Coordination",
        },
        {
          title: "The plan exists, but event day needs a lead",
          description:
            "Start with Day-of Event Management when the event is planned and the day needs someone managing the details.",
          service: "Day-of Event Management",
        },
      ],
    },
    servicesSupportCta: {
      eyebrow: "Need help choosing?",
      title: "Not sure where your event fits?",
      description:
        "Send the details you have. JPL Atelier will help you choose the service that makes the most sense.",
      href: "/inquiry",
      label: "Start an Inquiry",
    },
    processJourney: {
      eyebrow: "The planning experience",
      title: "How the planning flow unfolds",
      description:
        "The six-step flow keeps the look, space, vendors, and event day organized.",
      steps: [
        {
          title: "Step 1: Inquiry Form",
          description:
            "The intake captures the event type, priorities, needs, and expectations.",
        },
        {
          title: "Step 2: Moodboard",
          description:
            "The moodboard sets the palette, textures, styling, and atmosphere.",
        },
        {
          title: "Step 3: Space Planning",
          description:
            "The space is reviewed for flow, layout, focal points, and design opportunities.",
        },
        {
          title: "Step 4: Digital Decoration",
          description:
            "Digital previews show the decor direction before production begins.",
        },
        {
          title: "Step 5: Vendors",
          description:
            "Vendor coordination organizes quotes, responsibilities, timing, communication, and final confirmations.",
        },
        {
          title: "Step 6: Event Day",
          description:
            "On-site management watches setup, vendor flow, timeline, and final day-of details.",
        },
      ],
    },
    lookbookGallery: {
      eyebrow: "Visual language",
      title: "A look at the mood, restraint, and detail behind JPL Atelier.",
      description:
        "This page shows the studio’s taste and visual references while the completed-event portfolio continues to grow.",
      items: [
        {
          alt: "Neutral tablescape with editorial floral styling",
          caption:
            "Tablescape direction built around proportion, texture, and restraint.",
          src: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1200&q=80",
        },
        {
          alt: "Refined ceremony aisle with modern floral composition",
          caption:
            "Ceremony references for balance, spacing, and restrained floral composition.",
          src: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1200&q=80",
        },
        {
          alt: "Luxury reception table details with warm candlelight",
          caption: "Reception styling references for formal evening events.",
          src: "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?auto=format&fit=crop&w=1200&q=80",
        },
        {
          alt: "Elegant floral arrangement on a crisp white table",
          caption:
            "Palette and floral studies with warm neutrals and careful detail.",
          src: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=1200&q=80",
        },
        {
          alt: "Sophisticated place setting with layered neutral textures",
          caption:
            "Detail references for tone, materials, and the guest table experience.",
          src: "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=1200&q=80",
        },
        {
          alt: "Venue detail with editorial light and refined event styling",
          caption:
            "Spatial references for corporate events, luxury gatherings, and private celebrations.",
          src: "https://images.unsplash.com/photo-1513278974582-3e1b4a4fa21d?auto=format&fit=crop&w=1200&q=80",
        },
      ],
    },
  },
  inquiry: {
    eyebrow: "Start your event",
    title: "Share the details so we can point you to the right service.",
    description:
      "Every event comes with different needs. The right service depends on the event type, location, planning stage, design needs, vendors, guest flow, and day-of logistics.",
    asideTitle: "Before you inquire",
    availability: [
      "JPL Atelier focuses on weddings, corporate parties, luxury events, and senior celebrations 18+.",
      "Share the details you already know: date, location, guest count, event type, vendors, and where you need help.",
    ],
    formEyebrow: "Inquiry form",
    formIntro:
      "Fill out the form with your event details and planning priorities.",
    privacyNote:
      "Your inquiry is saved so the planning team can review the details and follow up about fit, scope, and next steps.",
    submitError:
      "The inquiry could not be submitted right now. Please try again.",
    submitPending: "Submitting inquiry...",
    submitLabel: "Submit Inquiry",
    submitSuccess:
      "Your inquiry was sent. The team can now review your event details and follow up.",
    successHint:
      "Next, the studio reviews your event type, planning needs, and level of help requested.",
    actions: [
      { href: "/services", label: "Review services" },
      { href: "/process", label: "View the process" },
    ],
    fields: [
      {
        autocomplete: "name",
        label: "Name",
        name: "name",
        placeholder: "Your full name",
        required: true,
        type: "text",
      },
      {
        autocomplete: "email",
        label: "Email",
        name: "email",
        placeholder: "name@example.com",
        required: true,
        type: "email",
      },
      {
        autocomplete: "tel",
        label: "Phone",
        name: "phone",
        placeholder: "(787) 000-0000",
        required: true,
        type: "tel",
      },
      {
        label: "Event type",
        name: "eventType",
        options: [
          { label: "Select event type", value: "" },
          { label: "Wedding", value: "Wedding" },
          { label: "Corporate Party", value: "Corporate Party" },
          { label: "Luxury Event", value: "Luxury Event" },
          { label: "Senior Celebration 18+", value: "Senior Celebration 18+" },
        ],
        required: true,
        type: "select",
      },
    ],
  },
  legal: {
    description:
      "This page explains how JPL Atelier uses inquiry information and what the website content is meant to cover.",
    sections: [
      {
        title: "Privacy Notice",
        body: [
          "Information sent through the inquiry form is used to review event requests, respond to messages, and continue planning conversations related to JPL Atelier services.",
          "Details are shared only with providers needed to operate the website, manage communications, or support the inquiry process.",
          "To update or remove information previously shared through the site, contact the business directly using the contact method provided by the studio.",
        ],
      },
      {
        title: "Terms",
        body: [
          "Website content is general information about services, process, event focus, and brand direction. Sending an inquiry does not create a booking, contract, or guarantee of availability.",
          "Project scope, pricing, timelines, and deliverables are confirmed later through direct communication and any final agreement approved by both parties.",
          "JPL Atelier may update website content, service descriptions, or this page when business details or operating practices change.",
        ],
      },
    ],
    title: "Privacy and terms",
    updatedAt: "2026-05-08",
  },
};
